// src/components/common/SettingsPage.jsx
import { useLocation } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider,
  IconButton, InputAdornment, Paper,
  Radio, Skeleton, Stack, TextField, Typography, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Slide,
  AppBar, Toolbar, Container, Card, CardContent, Tooltip,
  Switch, Tabs, Tab, Autocomplete,
} from '@mui/material';

// Icons
import SearchIcon        from '@mui/icons-material/Search';
import ClearIcon         from '@mui/icons-material/Clear';
import EditIcon          from '@mui/icons-material/Edit';
import TableRowsIcon     from '@mui/icons-material/TableRows';
import DeleteIcon        from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ListAltIcon       from '@mui/icons-material/ListAlt';
import CloseIcon         from '@mui/icons-material/Close';
import LockIcon          from '@mui/icons-material/Lock';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon        from '@mui/icons-material/Person';
import SettingsIcon      from '@mui/icons-material/Settings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import HowToRegIcon      from '@mui/icons-material/HowToReg';
import BusinessIcon      from '@mui/icons-material/Business';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import GroupIcon         from '@mui/icons-material/Group';

// Contexts & Hooks
import { useAuth }        from '../../contexts/AuthContext';
import { useToast }       from '../../contexts/ToastContext';
import { useTheme }       from '../../contexts/ThemeContext';

// Services
import { preferencesService }     from '../../services/preferencesService';
import {
  singleValueListService,
  STATIC_FALLBACK_LISTS,
  APPROVER_LIST_NAME,
  APPROVAL_STATUS_APPROVED,
  TICKET_RECEIVERS_LIST_NAME,
  RECEIVER_ROLE_OPTIONS,
  NON_RECEIVER_ROLES,
  isTicketReceiversList as svlIsTicketReceiversList,
} from '../../services/singleValueListService';

import { userService }    from '../../services/userService';
import CompanyProfileManager from '../admin/CompanyProfileManager';

export const ASSETS_PAGE_SIZE_PREF_NAME = '__assets_page_size__';
export const ASSETS_PAGE_SIZE_PREF_TYPE = '__system__';

// ─── UTILS ──────────────────────────────────────────────────────────────────
const formatLabel = (raw) => {
  if (raw == null || raw === '') return '—';
  const str = String(raw).trim();
  if (!str.includes('_') && !str.includes('-')) {
    return str
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return str
    .replace(/^_+|_+$/g, '')
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const slugify = (str) =>
  String(str || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const hasRealValue = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'object') {
    const hasLevel = Number.isFinite(Number(v.level)) && Number(v.level) >= 1;
    const hasUsers = Array.isArray(v.users) && v.users.length > 0 && v.users.some((e) => String(e).includes('@'));
    const hasEmail = typeof v.email === 'string' && v.email.includes('@');
    return hasLevel && (hasUsers || hasEmail);
  }
  return false;
};

const countRealItems = (items = []) =>
  items.filter((it) => it && (it.label?.trim() || hasRealValue(it.value))).length;

const SYSTEM_FALLBACK_IDS = new Set(STATIC_FALLBACK_LISTS.map((l) => l.id));
const isSystemFallbackList = (list) => {
  if (!list) return false;
  if (list._isSystemFallback === true) return true;
  if (list.scope === 'system' && SYSTEM_FALLBACK_IDS.has(list.id)) return true;
  return false;
};

// Identifies the AssetOwner bypass list
const isAssetOwnerList = (list) => {
  if (!list) return false;
  const name = String(list.name || '').toLowerCase();
  const id = String(list.id || '').toLowerCase();
  return (
    name === 'assetowner' ||
    name === 'asset_owner' ||
    id.includes('asset-owner') ||
    list.scope === 'assetOwner'
  );
};

// Identifies the Approver list
const isApproverList = (list) => {
  if (!list) return false;
  const name = String(list.name || '').toLowerCase();
  const id = String(list.id || '').toLowerCase();
  return (
    name === 'approver' ||
    name === 'approvers' ||
    id.includes('approver') ||
    list.scope === 'approver'
  );
};

// Identifies the Ticket Receivers catalog
const isTicketReceiversList = (list) => {
  if (list && svlIsTicketReceiversList) {
    try { if (svlIsTicketReceiversList(list)) return true; } catch { /* noop */ }
  }
  if (!list) return false;
  const name = String(list.name || '').toLowerCase();
  const id = String(list.id || '').toLowerCase();
  return name === TICKET_RECEIVERS_LIST_NAME || id.includes('ticket-receivers');
};

// ── Helper: ensures the last approver row always has status "Approved" ───────
const enforceLastApproverStatus = (items = [], isApproverCatalog = false) => {
  if (!isApproverCatalog || items.length === 0) return items;
  const lastIdx = items.length - 1;
  return items.map((it, idx) => {
    if (idx !== lastIdx) return it;
    const existingLevel =
      it.value && typeof it.value === 'object' ? (Number(it.value.level) || 1) : 1;
    const existingUsers =
      it.value && typeof it.value === 'object' && Array.isArray(it.value.users)
        ? it.value.users
        : [];
    return {
      ...it,
      value: {
        level: existingLevel,
        users: existingUsers,
        status: APPROVAL_STATUS_APPROVED,
      },
    };
  });
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── SKELETONS ──────────────────────────────────────────────────────────────
const SettingsSectionSkeleton = ({ fieldCount = 1, hasButton = false }) => (
  <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Box sx={{ px: 3, py: 2, bgcolor: 'background.neutral', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Skeleton variant="text" width={180} height={24} sx={{ borderRadius: 1 }} />
      <Skeleton variant="text" width={260} height={18} sx={{ borderRadius: 1, mt: 0.5 }} />
    </Box>
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        {Array.from({ length: fieldCount }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={40} sx={{ borderRadius: 2 }} />
        ))}
        {hasButton && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Skeleton variant="rounded" width={180} height={36} sx={{ borderRadius: 2 }} />
          </Box>
        )}
      </Stack>
    </Box>
  </Paper>
);

const SettingsPageSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box>
      <Skeleton variant="text" width={160} height={40} sx={{ borderRadius: 1 }} />
      <Skeleton variant="text" width={340} height={20} sx={{ borderRadius: 1, mt: 0.5 }} />
    </Box>
    <Box sx={{ maxWidth: 800, width: '100%' }}>
      <Stack spacing={3}>
        <SettingsSectionSkeleton fieldCount={1} hasButton={false} />
        <SettingsSectionSkeleton fieldCount={3} hasButton={true} />
      </Stack>
    </Box>
  </Container>
);

// ─── REUSABLE COMPONENTS ────────────────────────────────────────────────────
const SettingsSection = ({ title, description, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Box sx={{ px: 3, py: 2, bgcolor: 'background.neutral', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const PreferenceListItem = ({ icon, tag, title, subtitle, metaValue, onEdit, onDelete }) => {
  const normalizedTag = String(tag || '').toLowerCase();
  const isSystem = normalizedTag === 'system';

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 2, px: 3, borderBottom: '1px solid', borderColor: 'divider',
        transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'primary.50', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.2 }}>
              {formatLabel(title)}
            </Typography>
            <Chip
              label={formatLabel(tag)}
              size="small"
              sx={{
                height: 20, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                bgcolor: isSystem ? 'primary.50' : 'action.selected',
                color: isSystem ? 'primary.dark' : 'text.secondary',
                borderRadius: 1,
              }}
            />
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, ml: 2 }}>
        {metaValue && (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {metaValue}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onEdit && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit} color="primary" sx={{ bgcolor: 'action.hover' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete} color="error" sx={{ bgcolor: 'error.50', '&:hover': { bgcolor: 'error.100' } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── SUB-MODAL: ASSETS PAGE SIZE ──────────────────────────────────────────
const AssetsPageSizeManager = ({ currentUser, showToast }) => {
  const MIN_PAGE_SIZE = 1;
  const MAX_PAGE_SIZE = 100000;
  const QUICK_PICKS = [50, 100, 200, 500, 1000];

  const [savedSize,    setSavedSize]    = useState(50);
  const [loading,      setLoading]      = useState(true);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [selectedSize, setSelectedSize] = useState(50);
  const [inputValue,   setInputValue]   = useState('50');
  const [isAllMode,    setIsAllMode]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const loadSetting = useCallback(async () => {
    if (!currentUser?.uid && !currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const allPrefs = await preferencesService.getAllIncludingSystem(currentUser);
      const existing = allPrefs.find(
        (p) =>
          p.preferenceType === ASSETS_PAGE_SIZE_PREF_TYPE &&
          p.preferenceName === ASSETS_PAGE_SIZE_PREF_NAME &&
          p.deleted !== true,
      );
      if (existing) {
        const val    = parseInt(existing.preferenceValue, 10);
        const parsed = isNaN(val) ? 50 : val;
        setSavedSize(parsed);
      } else {
        setSavedSize(50);
      }
    } catch (err) {
      console.warn('AssetsPageSize load error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadSetting(); }, [loadSetting]);

  const handleOpenModal = () => {
    if (savedSize === -1) {
      setIsAllMode(true);
      setInputValue('');
      setSelectedSize(-1);
    } else {
      setIsAllMode(false);
      setInputValue(String(savedSize));
      setSelectedSize(savedSize);
    }
    setError('');
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    setInputValue(raw);
    setError('');
    if (raw === '') { setSelectedSize(0); return; }
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) { setError('Please enter a valid number'); return; }
    if (parsed < MIN_PAGE_SIZE) { setError(`Minimum is ${MIN_PAGE_SIZE}`); setSelectedSize(parsed); return; }
    if (parsed > MAX_PAGE_SIZE) { setError(`Maximum is ${MAX_PAGE_SIZE.toLocaleString()}`); setSelectedSize(parsed); return; }
    setSelectedSize(parsed);
  };

  const applyQuickPick = (value) => {
    setIsAllMode(false);
    setSelectedSize(value);
    setInputValue(String(value));
    setError('');
  };

  const handleSave = async () => {
    if (saving) return;
    if (!isAllMode) {
      if (inputValue === '' || isNaN(parseInt(inputValue, 10))) {
        setError('Please enter a valid number');
        return;
      }
      const parsed = parseInt(inputValue, 10);
      if (parsed < MIN_PAGE_SIZE || parsed > MAX_PAGE_SIZE) {
        setError(`Must be between ${MIN_PAGE_SIZE} and ${MAX_PAGE_SIZE.toLocaleString()}`);
        return;
      }
    }

    setSaving(true);
    try {
      const allPrefs = await preferencesService.getAllIncludingSystem(currentUser);
      const existing = allPrefs.find(
        (p) =>
          p.preferenceType === ASSETS_PAGE_SIZE_PREF_TYPE &&
          p.preferenceName === ASSETS_PAGE_SIZE_PREF_NAME &&
          p.deleted !== true,
      );

      const valueToSave = isAllMode ? -1 : selectedSize;
      const payload = {
        preferenceType:  ASSETS_PAGE_SIZE_PREF_TYPE,
        preferenceName:  ASSETS_PAGE_SIZE_PREF_NAME,
        preferenceValue: String(valueToSave),
      };

      if (existing?.id) {
        await preferencesService.update(currentUser, existing.id, payload, { allowSystem: true });
      } else {
        await preferencesService.create(currentUser, payload, { allowSystem: true });
      }

      setSavedSize(valueToSave);
      showToast('Assets page size saved!', 'success');
      setModalOpen(false);
    } catch (err) {
      showToast(`Failed to save: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const hasError = Boolean(error);
  const canSave  = !saving && !hasError && (isAllMode || (inputValue !== '' && !isNaN(parseInt(inputValue, 10))));

  return (
    <>
      {loading ? (
        <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 0, borderBottom: '1px solid #eee' }} />
      ) : (
        <PreferenceListItem
          icon={<TableRowsIcon fontSize="small" />}
          tag="System"
          title="Assets Page Size"
          subtitle="Default number of rows shown on the Assets list"
          metaValue={savedSize === -1 ? 'All Rows' : `${savedSize} rows`}
          onEdit={handleOpenModal}
        />
      )}

      <Dialog open={modalOpen} onClose={() => !saving && setModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Edit Default Page Size</Typography>
            <Typography variant="caption" color="text.secondary">Set the default number of items shown on the assets table.</Typography>
          </Box>
          <IconButton size="small" onClick={() => !saving && setModalOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small" type="number" label="Rows per page"
                value={inputValue} onChange={handleInputChange}
                disabled={isAllMode || saving} error={hasError}
                helperText={
                  hasError ? error
                  : isAllMode ? 'Disabled — "Show All" is active'
                  : `Range: ${MIN_PAGE_SIZE}–${MAX_PAGE_SIZE.toLocaleString()}`
                }
                inputProps={{ min: MIN_PAGE_SIZE, max: MAX_PAGE_SIZE, step: 1 }}
                sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Box
                onClick={() => { if (!saving) { setIsAllMode(!isAllMode); setError(''); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.75, mt: 0.25, borderRadius: 2, border: '1px solid',
                  borderColor: isAllMode ? 'primary.main' : 'divider',
                  bgcolor: isAllMode ? 'primary.50' : 'transparent',
                  cursor: saving ? 'default' : 'pointer', height: 40,
                }}
              >
                <Radio checked={isAllMode} size="small" sx={{ p: 0.5 }} disableRipple />
                <Typography variant="body2" fontWeight={isAllMode ? 700 : 500}>
                  Show All (no pagination)
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                Quick pick:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {QUICK_PICKS.map((val) => {
                  const isActive = selectedSize === val && !isAllMode;
                  return (
                    <Chip
                      key={val} label={val} size="small"
                      variant={isActive ? 'filled' : 'outlined'}
                      color={isActive ? 'primary' : 'default'}
                      onClick={() => applyQuickPick(val)} disabled={saving}
                      sx={{ fontWeight: isActive ? 700 : 500, minWidth: 56 }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" disableElevation
            disabled={!canSave}
            onClick={handleSave}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── ASSET OWNERSHIP MANAGER ──────────────────────────────────────────────
const AssetOwnershipManager = ({ currentUser, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [enabled, setEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    if (!currentUser?.uid && !currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const isEnabled = await preferencesService.isOwnershipEnabled(currentUser);
      setEnabled(isEnabled);
    } catch (err) {
      console.warn('AssetOwnership load error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleToggle = async (newValue) => {
    if (!currentUser) {
      showToast('Not authenticated', 'error');
      return;
    }
    setSaving(true);
    try {
      await preferencesService.setOwnershipPreference(currentUser, newValue);
      setEnabled(newValue);
      showToast(
        newValue
          ? 'Asset ownership capture enabled'
          : 'Asset ownership capture disabled',
        'success',
      );
    } catch (err) {
      showToast(err.message || 'Failed to update preference', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        height={72}
        sx={{ borderRadius: 0, borderBottom: '1px solid #eee' }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: 2,
        bgcolor: 'action.hover',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'primary.50', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <SupervisorAccountIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Capture Asset Owner
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {enabled
              ? 'Ownership field will be shown on asset create/edit forms.'
              : 'Ownership field will be hidden.'}
          </Typography>
        </Box>
      </Box>
      <Switch
        checked={enabled}
        onChange={(e) => handleToggle(e.target.checked)}
        disabled={saving}
        color="primary"
      />
    </Box>
  );
};

// ─── APPROVER MANAGER ───────────────────────────────────────────────────────
const ApproverManager = ({ currentUser, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [enabled, setEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    if (!currentUser?.uid && !currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const isEnabled = await preferencesService.isApproverEnabled(currentUser);
      setEnabled(isEnabled);
    } catch (err) {
      console.warn('Approver load error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleToggle = async (newValue) => {
    if (!currentUser) {
      showToast('Not authenticated', 'error');
      return;
    }
    setSaving(true);
    try {
      await preferencesService.setApproverPreference(currentUser, newValue);
      setEnabled(newValue);
      showToast(
        newValue
          ? 'Approver workflow enabled'
          : 'Approver workflow disabled',
        'success',
      );
    } catch (err) {
      showToast(err.message || 'Failed to update preference', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        height={72}
        sx={{ borderRadius: 0, borderBottom: '1px solid #eee' }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: 2,
        bgcolor: 'action.hover',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'primary.50', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <HowToRegIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Enable Approval Workflow
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {enabled
              ? 'Approval workflow selector will appear on asset create/edit forms (until approved).'
              : 'Approval workflow selector will be hidden.'}
          </Typography>
        </Box>
      </Box>
      <Switch
        checked={enabled}
        onChange={(e) => handleToggle(e.target.checked)}
        disabled={saving}
        color="primary"
      />
    </Box>
  );
};

// ─── PUBLIC QR ACCESS MANAGER ──────────────────────────────────────────────
const PublicQrAccessManager = ({ currentUser, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [enabled, setEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    if (!currentUser?.uid && !currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const isEnabled = await preferencesService.isPublicQrEnabled(currentUser);
      setEnabled(isEnabled);
    } catch (err) {
      console.warn('PublicQR load error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleToggle = async (newValue) => {
    if (!currentUser) { showToast('Not authenticated', 'error'); return; }
    setSaving(true);
    try {
      await preferencesService.setPublicQrPreference(currentUser, newValue);
      setEnabled(newValue);
      showToast(
        newValue
          ? 'Public QR guest access enabled'
          : 'Public QR guest access disabled',
        'success',
      );
    } catch (err) {
      showToast(err.message || 'Failed to update preference', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Skeleton
        variant="rectangular" height={72}
        sx={{ borderRadius: 0, borderBottom: '1px solid #eee' }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 2, borderRadius: 2, bgcolor: 'action.hover', gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'primary.50', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <VisibilityIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Allow Public QR Access
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {enabled
              ? 'Anyone scanning a QR can view basic asset info as a Guest (login optional for tagging).'
              : 'Users must log in before viewing any asset details via QR.'}
          </Typography>
        </Box>
      </Box>
      <Switch
        checked={enabled}
        onChange={(e) => handleToggle(e.target.checked)}
        disabled={saving}
        color="primary"
      />
    </Box>
  );
};

// ─── DUPLICATE TICKET POLICY MANAGER ───────────────────────────────────────
const DuplicateTicketPolicyManager = ({ currentUser, showToast }) => {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [enabled, setEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    if (!currentUser?.uid && !currentUser?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const isAllowed = await preferencesService.isDuplicateTicketAllowed(currentUser);
      setEnabled(isAllowed);
    } catch (err) {
      console.warn('DuplicateTicketPolicy load error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleToggle = async (newValue) => {
    if (!currentUser) { showToast('Not authenticated', 'error'); return; }
    setSaving(true);
    try {
      await preferencesService.setDuplicateTicketPreference(currentUser, newValue);
      setEnabled(newValue);
      showToast(
        newValue
          ? 'Multiple open tickets per asset now allowed'
          : 'One open ticket per asset enforced',
        'success',
      );
    } catch (err) {
      showToast(err.message || 'Failed to update preference', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Skeleton
        variant="rectangular" height={72}
        sx={{ borderRadius: 0, borderBottom: '1px solid #eee' }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 2, borderRadius: 2, bgcolor: 'action.hover', gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'primary.50', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <ConfirmationNumberIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Allow Multiple Open Tickets per Asset
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {enabled
              ? 'Users can raise new tickets for an asset even while a previous ticket is Open or In Progress.'
              : 'Users must close the existing ticket for an asset before raising another one (recommended).'}
          </Typography>
        </Box>
      </Box>
      <Switch
        checked={enabled}
        onChange={(e) => handleToggle(e.target.checked)}
        disabled={saving}
        color="primary"
      />
    </Box>
  );
};

// ─── SUB-MODAL: EDIT LIST ITEMS MODAL ───────────────────────────────────────
const EditListItemsModal = ({ open, list, onClose, onSave, saving, currentUser }) => {
  const isNewList = Boolean(list && !list.id);
  const isSystemFallback = isSystemFallbackList(list);
  const isApproverCatalog = isApproverList(list);
  const isReceiversCatalog = isTicketReceiversList(list);

  const [listName, setListName] = useState('');
  const [listDesc, setListDesc] = useState('');
  const [items, setItems]       = useState([]);

  // Multi-selected roles for Receivers catalog
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Users fetched from user collection for multi-selection (Approvers)
  const [users, setUsers]               = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    if (open && list) {
      setListName(list.name || '');
      setListDesc(list.descriptions || '');

      // Special handling for Ticket Receivers
      if (isTicketReceiversList(list)) {
        const existingRoles = (list.items || [])
          .map((it) => String(it.value || it.label || '').trim().toLowerCase())
          .filter((r) => r && !NON_RECEIVER_ROLES.includes(r));
        setSelectedRoles(existingRoles);
        setItems(list.items || []);
        return;
      }

      let rawItems = (list.items || []).map((i) => {
        if (i?.value && typeof i.value === 'object') {
          const usersList = Array.isArray(i.value.users)
            ? [...i.value.users]
            : (i.value.email ? [i.value.email] : []);
          return {
            ...i,
            label: i.label || '',
            value: {
              level: Number(i.value.level) || 1,
              users: usersList,
              status: i.value.status || '',
            },
          };
        }
        if (isApproverCatalog && typeof i?.value === 'string' && i.value.includes('@')) {
          return {
            ...i,
            label: i.label || i.value,
            value: {
              level: 1,
              users: [i.value.trim().toLowerCase()],
              status: '',
            },
          };
        }
        return { ...i };
      });

      if (isApproverCatalog) {
        rawItems.sort((a, b) => (Number(a.value?.level) || 1) - (Number(b.value?.level) || 1));
      }

      rawItems = enforceLastApproverStatus(rawItems, isApproverCatalog);

      setItems(rawItems);
    }
  }, [open, list, isApproverCatalog]);

  // Load users when editing approvers catalog
  useEffect(() => {
    if (!open || !isApproverCatalog) return;
    let cancelled = false;
    setUsersLoading(true);

    userService
      .fetchUsers({ currentUser })
      .then((rows) => {
        if (cancelled) return;
        const listData = Array.isArray(rows) ? rows : (rows?.data ?? []);
        setUsers(listData);
      })
      .catch((err) => {
        console.warn('[EditListItemsModal] users load failed:', err);
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isApproverCatalog, currentUser]);

  if (!list) return null;

  const update = (idx, field, value) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const setApproverLevel = (idx, levelRaw) => {
    const n = parseInt(levelRaw, 10);
    const level = isNaN(n) ? 1 : Math.max(1, n);

    setItems((prev) => {
      const updated = prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              value: {
                ...(row.value && typeof row.value === 'object' ? row.value : {}),
                level,
                users: Array.isArray(row.value?.users) ? row.value.users : [],
                status: row.value?.status || '',
              },
            }
          : row
      );
      const sorted = [...updated].sort(
        (a, b) => (Number(a.value?.level) || 1) - (Number(b.value?.level) || 1)
      );
      return enforceLastApproverStatus(sorted, isApproverCatalog);
    });
  };

  const setApproverUsers = (idx, userEmails) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const existingLevel =
          it.value && typeof it.value === 'object' ? it.value.level : 1;
        const existingStatus =
          it.value && typeof it.value === 'object' ? (it.value.status || '') : '';
        return {
          ...it,
          value: {
            level: Number(existingLevel) || 1,
            users: Array.from(new Set(userEmails.map((e) => String(e).trim().toLowerCase()))),
            status: existingStatus,
          },
        };
      })
    );
  };

  const setApproverStatus = (idx, statusRaw) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        if (idx === prev.length - 1 && isApproverCatalog) return it;
        const existingLevel =
          it.value && typeof it.value === 'object' ? it.value.level : 1;
        const existingUsers =
          it.value && typeof it.value === 'object' && Array.isArray(it.value.users)
            ? it.value.users
            : [];
        return {
          ...it,
          value: {
            level: Number(existingLevel) || 1,
            users: existingUsers,
            status: String(statusRaw || ''),
          },
        };
      })
    );
  };

  const addRow = () => {
    setItems((prev) => {
      const maxLevel = prev.reduce((max, item) => {
        const lvl = Number(item.value?.level) || 0;
        return Math.max(max, lvl);
      }, 0);

      const newLevel = maxLevel + 1;

      const next = [
        ...prev,
        isApproverCatalog
          ? { label: '', value: { level: newLevel, users: [], status: '' } }
          : { label: '', value: '' },
      ];

      if (isApproverCatalog) {
        next.sort((a, b) => (Number(a.value?.level) || 1) - (Number(b.value?.level) || 1));
      }

      return enforceLastApproverStatus(next, isApproverCatalog);
    });
  };

  const isRowComplete = (it) => {
    if (!it?.label?.trim()) return false;
    const v = it.value;
    if (typeof v === 'string') return v.trim() !== '';
    if (v && typeof v === 'object') {
      const levelValid = Number.isFinite(Number(v.level)) && Number(v.level) >= 1;
      const usersValid = Array.isArray(v.users) && v.users.length > 0 && v.users.every((e) => String(e).includes('@'));
      const emailValid = typeof v.email === 'string' && v.email.includes('@');
      return levelValid && (usersValid || emailValid);
    }
    return false;
  };

  const isRowEmpty = (it) => {
    const labelEmpty = !it?.label?.trim();
    const v = it?.value;
    const valueEmpty =
      v == null ||
      (typeof v === 'string' && v.trim() === '') ||
      (typeof v === 'object' && (!v.users || v.users.length === 0) && !v.email && v.level == null);
    return labelEmpty && valueEmpty;
  };

  const noPartialRows = items.every((it) => isRowComplete(it) || isRowEmpty(it));
  const completeCount = items.filter(isRowComplete).length;

  const nameEditable = isNewList;

  // Receivers → valid if at least one role selected (empty allowed too → means default/all)
  const valid = isReceiversCatalog
    ? true
    : (nameEditable ? listName.trim() !== '' : true) &&
      items.length > 0 &&
      completeCount > 0 &&
      noPartialRows;

  const displayName = formatLabel(list.name);

  const handleSaveClick = () => {
    if (isReceiversCatalog) {
      // Clean + filter out non-receiver roles like "user"
      const cleanRoles = Array.from(
        new Set(
          (selectedRoles || [])
            .map((r) => String(r || '').trim().toLowerCase())
            .filter((r) => r && !NON_RECEIVER_ROLES.includes(r)),
        ),
      );
      const normalizedItems = cleanRoles.map((r) => {
        const opt = RECEIVER_ROLE_OPTIONS.find(
          (o) => o.value.toLowerCase() === r,
        );
        return { label: opt?.label || r, value: r };
      });
      onSave({
        name: list.name,
        descriptions: listDesc,
        scope: list.scope || 'company',
        items: normalizedItems,
      });
      return;
    }

    const finalItems = enforceLastApproverStatus(items, isApproverCatalog);
    setItems(finalItems);

    onSave({
      name: nameEditable ? slugify(listName) : list.name,
      descriptions: listDesc,
      scope: list.scope || 'company',
      items: finalItems,
    });
  };

  const headerTitle = isNewList
    ? 'Create New List'
    : isSystemFallback
      ? `Override System List: ${displayName}`
      : `Edit List Items: ${displayName}`;

  const headerSubtitle = isNewList
    ? 'Define a new category and values'
    : isSystemFallback
      ? 'Changes will create a company-level override of this system list'
      : 'Update the items below and submit to save changes';

  const saveButtonLabel = isNewList
    ? 'Create List'
    : isSystemFallback
      ? 'Create Override'
      : 'Save Items';

  const isLastApproverRow = (idx) => isApproverCatalog && idx === items.length - 1;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!saving ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      <AppBar sx={{ position: 'relative', flexShrink: 0, bgcolor: 'primary.main' }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{headerTitle}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{headerSubtitle}</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', py: 4 }}>
          <Container maxWidth="lg">
            {isSystemFallback && !isApproverCatalog && !isReceiversCatalog && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                You are overriding a <strong>system-defined</strong> list.
                A new company-scoped copy will be created and will take precedence over the defaults.
              </Alert>
            )}

            {isApproverCatalog && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Configure approval levels (1, 2, 3, …). For each row, set a <strong>Level</strong>, enter a custom
                <strong> Label</strong>, set the <strong>Status</strong> shown after this level approves, and select
                one or more <strong>Users</strong>. The <strong>last row's Status is always locked to "Approved"</strong>.
              </Alert>
            )}

            {isReceiversCatalog && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Select the <strong>roles</strong> that are allowed to receive tickets. Users with any of these roles
                will appear as valid ticket receivers. The <strong>"User"</strong> role is intentionally excluded.
                Leaving this empty means <strong>all eligible roles</strong> can receive tickets.
              </Alert>
            )}

            <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b', mb: 3, pb: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                  List Information
                </Typography>

                <Stack spacing={2.5} mb={5}>
                  <TextField
                    fullWidth
                    label="List Name (Key)"
                    value={nameEditable ? listName : (list.name || '')}
                    onChange={(e) => setListName(e.target.value)}
                    size="small"
                    disabled={!nameEditable}
                    required={nameEditable}
                    helperText={
                      nameEditable
                        ? "Will be slugified (e.g., 'disposal_reasons')"
                        : "List names cannot be changed once created."
                    }
                  />
                                {!isApproverCatalog && !isReceiversCatalog && (
                    <TextField
                      fullWidth
                      label="Description"
                      value={listDesc}
                      onChange={(e) => setListDesc(e.target.value)}
                      size="small"
                      placeholder="Human-readable description of this list"
                    />
                  )}
                </Stack>

                {/* ─── TICKET RECEIVERS: ROLE MULTI-SELECT ─────────────── */}
                {isReceiversCatalog ? (
                  <Box>
                    <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
                        Allowed Receiver Roles
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Multi-select the roles permitted to receive tickets
                      </Typography>
                    </Box>

                    <Autocomplete
                      multiple
                      options={RECEIVER_ROLE_OPTIONS}
                      value={RECEIVER_ROLE_OPTIONS.filter((o) =>
                        selectedRoles.includes(o.value.toLowerCase())
                      )}
                      onChange={(e, newVal) => {
                        setSelectedRoles(
                          newVal.map((v) => String(v.value).toLowerCase()),
                        );
                      }}
                      getOptionLabel={(opt) => opt.label}
                      isOptionEqualToValue={(opt, val) =>
                        String(opt.value).toLowerCase() === String(val.value).toLowerCase()
                      }
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.value}
                            label={option.label}
                            size="small"
                            color="primary"
                            variant="filled"
                            icon={<GroupIcon />}
                            sx={{ borderRadius: 1, fontWeight: 600 }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            selectedRoles.length === 0
                              ? 'Select roles that can receive tickets...'
                              : ''
                          }
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: 1.5 },
                          }}
                        />
                      )}
                    />

                                       <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}
                    >
                      {selectedRoles.length === 0
                        ? 'No roles selected — all eligible roles will be allowed by default.'
                        : `${selectedRoles.length} role${selectedRoles.length === 1 ? '' : 's'} selected.`}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
                        List Items
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={addRow}
                        disabled={saving}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                      >
                        + Add Row
                      </Button>
                    </Box>

                    {items.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 1, px: 0.5 }}>
                        {isApproverCatalog && (
                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                            LEVEL
                          </Typography>
                        )}
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ ...(isApproverCatalog ? { width: 200, flexShrink: 0 } : { flex: 1 }) }}>
                          {isApproverCatalog ? 'APPROVER LABEL' : 'LABEL (displayed title in forms)'}
                        </Typography>
                        {isApproverCatalog && (
                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>
                            STATUS
                          </Typography>
                        )}
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>
                          {isApproverCatalog ? 'APPROVER USERS (multi-select options for this level)' : 'VALUE (stored key)'}
                        </Typography>
                        <Box sx={{ width: 40 }} />
                      </Box>
                    )}

                    <Stack spacing={2}>
                      {items.map((it, idx) => {
                        const isLocked =
                          !isNewList &&
                          !isSystemFallback &&
                          !isApproverCatalog &&
                          Boolean(it.value);

                        const levelValue =
                          it.value && typeof it.value === 'object'
                            ? (it.value.level ?? 1)
                            : 1;

                        const isLastRow  = isLastApproverRow(idx);
                        const isStatusLocked = isApproverCatalog && isLastRow;

                        const statusValue = isStatusLocked
                          ? APPROVAL_STATUS_APPROVED
                          : (it.value && typeof it.value === 'object' ? (it.value.status || '') : '');

                        const currentUsersList =
                          it.value && typeof it.value === 'object'
                            ? (Array.isArray(it.value.users) ? it.value.users : (it.value.email ? [it.value.email] : []))
                            : [];

                        const selectedUserObjects = currentUsersList.map((email) => {
                          const found = users.find(
                            (u) => String(u.email || '').toLowerCase() === String(email).toLowerCase()
                          );
                          return found || { email, name: email, displayName: email };
                        });

                        return (
                          <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            {isApproverCatalog && (
                              <TextField
                                size="small"
                                type="number"
                                label="Level"
                                value={levelValue}
                                onChange={(e) => setApproverLevel(idx, e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                sx={{ width: 100, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                              />
                            )}

                            <TextField
                              size="small"
                              placeholder={isApproverCatalog ? 'e.g., Reviewer, Finance Head' : 'e.g., Obsolete Technology'}
                              value={it.label || ''}
                              onChange={(e) => update(idx, 'label', e.target.value)}
                              sx={{
                                ...(isApproverCatalog ? { width: 200, flexShrink: 0 } : { flex: 1 }),
                                '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                              }}
                            />

                            {isApproverCatalog && (
                              <TextField
                                size="small"
                                placeholder={isStatusLocked ? '' : 'e.g., Reviewed, Finance Approved'}
                                value={statusValue}
                                onChange={(e) => setApproverStatus(idx, e.target.value)}
                                disabled={isStatusLocked}
                                sx={{
                                  width: 200,
                                  flexShrink: 0,
                                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                                }}
                                helperText={
                                  isStatusLocked
                                    ? 'Locked — final approval status'
                                    : (isLastRow ? 'Final approval status (editable)' : '')
                                }
                                FormHelperTextProps={{ sx: { mx: 0.5, fontSize: '0.68rem' } }}
                                InputProps={
                                  isStatusLocked
                                    ? {
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                          </InputAdornment>
                                        ),
                                      }
                                    : undefined
                                }
                              />
                            )}

                            {isApproverCatalog ? (
                              <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Autocomplete
                                  multiple
                                  size="small"
                                  options={users}
                                  loading={usersLoading}
                                  value={selectedUserObjects}
                                  onChange={(event, selectedOptions) => {
                                    const emails = selectedOptions
                                      .map((u) => (typeof u === 'string' ? u : u?.email))
                                      .filter(Boolean)
                                      .map((e) => String(e).trim().toLowerCase());
                                    setApproverUsers(idx, emails);
                                  }}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    const name = option.name || option.displayName || option.email || '';
                                    return option.email && name !== option.email
                                      ? `${name} (${option.email})`
                                      : name;
                                  }}
                                  isOptionEqualToValue={(option, val) => {
                                    const optEmail = String(option?.email || option || '').toLowerCase();
                                    const valEmail = String(val?.email || val || '').toLowerCase();
                                    return optEmail === valEmail;
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={
                                        currentUsersList.length === 0
                                          ? (usersLoading ? 'Loading users...' : 'Select eligible users...')
                                          : ''
                                      }
                                      InputProps={{
                                        ...params.InputProps,
                                        sx: { borderRadius: 1.5 },
                                        endAdornment: (
                                          <>
                                            {usersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, tagIdx) => {
                                      const name = typeof option === 'string' ? option : (option.name || option.displayName || option.email);
                                      return (
                                        <Chip
                                          {...getTagProps({ index: tagIdx })}
                                          key={option.email || tagIdx}
                                          label={name}
                                          size="small"
                                          sx={{ borderRadius: 1, fontWeight: 500 }}
                                        />
                                      );
                                    })
                                  }
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', mt: 0.5, mx: 0.5, color: '#6b7280', fontSize: '0.68rem' }}
                                >
                                  {currentUsersList.length === 1
                                    ? '1 user selected (will automatically default in creation form).'
                                    : `${currentUsersList.length} users selected (creator can choose from these).`}
                                </Typography>
                              </Box>
                            ) : (
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Tooltip
                                  title={
                                    isLocked
                                      ? 'Value is immutable after creation — assets reference this key.'
                                      : 'Lowercase slug recommended (e.g., obsolete-tech).'
                                  }
                                  arrow placement="top"
                                >
                                  <TextField
                                    size="small" fullWidth
                                    placeholder="Value (e.g., obsolete-tech)"
                                    value={typeof it.value === 'string' ? it.value : ''}
                                    onChange={(e) => update(idx, 'value', e.target.value)}
                                    disabled={isLocked}
                                    InputProps={{
                                      sx: { borderRadius: 1.5 },
                                      endAdornment: isLocked ? (
                                        <InputAdornment position="end">
                                          <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        </InputAdornment>
                                      ) : null,
                                    }}
                                    helperText={
                                      isLocked
                                        ? 'Locked — this is the stable business key'
                                        : 'Will be slugified on save'
                                    }
                                    FormHelperTextProps={{ sx: { mx: 0.5, fontSize: '0.68rem' } }}
                                  />
                                </Tooltip>
                              </Box>
                            )}

                            <Tooltip title="Delete disabled" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{
                                    bgcolor: 'action.disabledBackground',
                                    opacity: 0.4,
                                    width: 40,
                                    height: 40,
                                    flexShrink: 0,
                                    mt: 0.25,
                                    cursor: 'not-allowed',
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        );
                      })}
                      {items.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                          No preference items available in this category.
                        </Typography>
                      )}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Container>
        </Box>

        <Box sx={{ borderTop: '1px solid #e2e8f0', backgroundColor: '#fff', py: 2, px: 3, boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', flexShrink: 0 }}>
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
              <Button onClick={onClose} disabled={saving} sx={{ px: 3, py: 1, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontWeight: 500, textTransform: 'none', '&:hover': { backgroundColor: '#e5e7eb' } }}>
                Cancel
              </Button>
              <Button
                variant="contained" disableElevation
                disabled={!valid || saving}
                onClick={handleSaveClick}
                sx={{ px: 3, py: 1, backgroundColor: '#2563eb', color: '#fff', borderRadius: '6px', fontWeight: 600, textTransform: 'none', minWidth: '160px', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { backgroundColor: '#1d4ed8' } }}
              >
                {saving && <CircularProgress size={16} color="inherit" />}
                {saving ? 'Saving…' : saveButtonLabel}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </Dialog>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
const SettingsPage = () => {
  const { currentUser, updatePassword, loading: authLoading } = useAuth();
  const { theme, toggleTheme }          = useTheme();
  const { showToast }                   = useToast();

  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    Number.isInteger(location?.state?.tab) ? location.state.tab : 0
  );

  const [pageReady, setPageReady] = useState(false);
  const [loading, setLoading]     = useState(false);

  const [email, setEmail] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false, newPassword: false, confirmPassword: false,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [deletingList, setDeletingList] = useState(null);
  const [listSaving, setListSaving] = useState(false);

  const isAdmin = useMemo(() => {
    const role = currentUser?.role ? String(currentUser.role).toLowerCase() : '';
    return role === 'admin';
  }, [currentUser]);

  const isCompanyAdmin = useMemo(() => {
    const role = currentUser?.role ? String(currentUser.role).toLowerCase() : '';
    return role === 'admin' || role === 'locationadmin';
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;
    const MIN_SKELETON_MS = 350;
    const start = Date.now();

    if (currentUser !== undefined) {
      if (currentUser?.email) setEmail(currentUser.email);
      const remaining = MIN_SKELETON_MS - (Date.now() - start);
      setTimeout(() => { if (!cancelled) setPageReady(true); }, Math.max(remaining, 0));
    }
    return () => { cancelled = true; };
  }, [currentUser]);

  const loadLists = useCallback(async () => {
    if (!currentUser) return;
    setListsLoading(true);
    try {
      const rows = await singleValueListService.getLists() || [];
      const normalized = rows.map((l) => {
        const payload = typeof l.toPayload === 'function' ? l.toPayload() : { ...l };
        return {
          ...payload,
          _isSystemFallback:
            payload.scope === 'system' && SYSTEM_FALLBACK_IDS.has(payload.id),
        };
      });
      normalized.sort((a, b) => {
        if (a.scope !== b.scope) {
          if (a.scope === 'system') return -1;
          if (b.scope === 'system') return 1;
        }
        return (a.name || '').localeCompare(b.name || '');
      });
      setLists(normalized);
    } catch (err) {
      showToast(`Failed to load list overrides: ${err.message}`, 'error');
    } finally {
      setListsLoading(false);
    }
  }, [currentUser, showToast]);

  useEffect(() => {
    if (Number.isInteger(location?.state?.tab)) {
      setActiveTab(location.state.tab);
    }
  }, [location.state]);

  useEffect(() => {
    if (currentUser && activeTab === 2) {
      loadLists();
    }
  }, [currentUser, activeTab, loadLists]);

  const filteredLists = useMemo(() => {
    if (!lists) return [];
    const visible = lists.filter((l) => !isAssetOwnerList(l));

    if (!searchQuery.trim()) return visible;
    const q = searchQuery.toLowerCase().trim();
    return visible.filter((l) =>
      l.name?.toLowerCase().includes(q) ||
      l.descriptions?.toLowerCase().includes(q) ||
      formatLabel(l.name).toLowerCase().includes(q)
    );
  }, [lists, searchQuery]);

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast("Passwords don't match", 'error');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      showToast('New password must be at least 6 characters', 'error');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      showToast('Password updated successfully!', 'success');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswords({ currentPassword: false, newPassword: false, confirmPassword: false });
    } catch (error) {
      console.error('[SettingsPage] Password update error:', error);
      const errorMap = {
        'auth/invalid-credential':    'Current password is incorrect',
        'auth/wrong-password':        'Current password is incorrect',
        'auth/weak-password':         'New password is too weak',
        'auth/requires-recent-login': 'Please sign out and sign in again before changing password',
      };
      showToast(errorMap[error.code] || error.message || 'Failed to update password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveList = async (updatedData) => {
    setListSaving(true);
    try {
      const isSystemFallback = isSystemFallbackList(editingList);
      const isNew = !editingList?.id;
      const isApprover = isApproverList(editingList);
      const isReceivers = isTicketReceiversList(editingList);

      const targetScope = isSystemFallback ? 'company' : (editingList?.scope || 'company');

      // Receivers → go through the dedicated setter which handles filtering/normalization
      if (isReceivers) {
        const roles = (updatedData.items || [])
          .map((it) => String(it.value || it.label || '').trim().toLowerCase())
          .filter(Boolean);

        await singleValueListService.setTicketReceiverRoles(
          roles,
          currentUser?.companyId || '',
        );

        window.dispatchEvent(new CustomEvent('catalogsUpdated'));
        showToast('Ticket receiver roles updated successfully', 'success');
        setEditingList(null);
        loadLists();
        return true;
      }

      const cleanedItems = (updatedData.items || []).filter((it) => {
        if (!it?.label?.trim()) return false;
        const v = it.value;
        if (typeof v === 'string') return v.trim() !== '';
        if (v && typeof v === 'object') {
          const levelValid = Number.isFinite(Number(v.level)) && Number(v.level) >= 1;
          const hasUsers = Array.isArray(v.users) && v.users.length > 0;
          const hasEmail = typeof v.email === 'string' && v.email.includes('@');
          return levelValid && (hasUsers || hasEmail);
        }
        return false;
      });

      const SYSTEM_LIST_NAMES = new Set(
        STATIC_FALLBACK_LISTS.map((l) => l.name.toLowerCase())
      );
      const isSystemList = SYSTEM_LIST_NAMES.has(
        String(updatedData.name || '').toLowerCase()
      );

      const sortedItems = isApprover
        ? [...cleanedItems].sort(
            (a, b) => (Number(a.value?.level) || 1) - (Number(b.value?.level) || 1),
          )
        : cleanedItems;

      const finalApproverItems = isApprover
        ? enforceLastApproverStatus(sortedItems, true)
        : sortedItems;

      const payload = {
        name: updatedData.name,
        scope: targetScope,
        companyId: isSystemFallback
          ? (currentUser?.companyId || '')
          : (editingList?.companyId ?? currentUser?.companyId ?? ''),
        userId: editingList?.userId || null,

        items: finalApproverItems.map((it) => {
          if (isApprover && it.value && typeof it.value === 'object') {
            const usersList = Array.isArray(it.value.users)
              ? Array.from(new Set(it.value.users.map((e) => String(e).trim().toLowerCase()).filter((e) => e.includes('@'))))
              : (it.value.email ? [String(it.value.email).trim().toLowerCase()] : []);

            const status = String(it.value.status || '').trim();
            const value = {
              level: Number(it.value.level) || 1,
              users: usersList,
            };
            if (status) value.status = status;

            return { label: it.label.trim(), value };
          }

          const rawValue = String(it.value || '').trim();
          const value = isSystemList
            ? rawValue
            : (slugify(rawValue) || slugify(it.label));
          return { label: it.label, value };
        }),
      };

      const id =
        isSystemFallback || isNew
          ? `list-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
          : editingList.id;

      await singleValueListService.upsertList(id, payload, {
        isCreation: isNew || isSystemFallback,
      });

      window.dispatchEvent(new CustomEvent('catalogsUpdated'));

      showToast(
        isSystemFallback
          ? 'Override created successfully'
          : isNew
            ? 'List created successfully'
            : 'List updated successfully',
        'success'
      );
      setEditingList(null);
      loadLists();
      return true;
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
      return false;
    } finally {
      setListSaving(false);
    }
  };

  const handleDeleteList = async () => {
    try {
      await singleValueListService.deleteList(deletingList.id);
      window.dispatchEvent(new CustomEvent('catalogsUpdated'));
      showToast('List override deleted successfully', 'success');
      setDeletingList(null);
      loadLists();
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  };

  const renderPasswordField = ({ name, label, placeholder, autoComplete, minLength }) => (
    <TextField
      fullWidth
      size="small"
      name={name}
      label={label}
      type={showPasswords[name] ? 'text' : 'password'}
      placeholder={placeholder}
      value={passwordForm[name]}
      onChange={(e) => setPasswordForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))}
      required
      disabled={loading}
      autoComplete={autoComplete}
      inputProps={{ minLength }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setShowPasswords((prev) => ({ ...prev, [name]: !prev[name] }))}
              disabled={loading}
            >
              {showPasswords[name] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  );

  if (!pageReady) return <SettingsPageSkeleton />;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account attributes, preferences, security keys, and system metadata
        </Typography>
      </Box>

      {isCompanyAdmin && (
        <Tabs
          value={activeTab}
          onChange={(e, newVal) => setActiveTab(newVal)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label="Account & Security" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab icon={<SettingsIcon fontSize="small" />} iconPosition="start" label="Preferences" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab icon={<ListAltIcon fontSize="small" />} iconPosition="start" label="Dropdown Catalogs" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab icon={<BusinessIcon fontSize="small" />} iconPosition="start" label="Company Profile" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
      )}

      <Box sx={{ maxWidth: (activeTab === 2 || activeTab === 3) ? '100%' : 800, width: '100%' }}>
        {activeTab === 0 && (
          <Stack spacing={3.5}>
            <SettingsSection title="Account Information" description="Your authenticated credentials identifier">
              <TextField
                fullWidth
                size="small"
                label="Email Address"
                value={email}
                InputProps={{ readOnly: true }}
                helperText="Email is managed by administrator authority. Contact technical support if modifications are required."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </SettingsSection>

            <SettingsSection title="Change Password" description="Update your security encryption keys">
              <form onSubmit={handleSubmitPassword}>
                <Stack spacing={2.5}>
                  {renderPasswordField({
                    name: 'currentPassword',
                    label: 'Current Password',
                    placeholder: 'Enter current password',
                    autoComplete: 'current-password',
                  })}
                  {renderPasswordField({
                    name: 'newPassword',
                    label: 'New Password',
                    placeholder: 'Enter new password (min 6 characters)',
                    autoComplete: 'new-password',
                    minLength: 6,
                  })}
                  {renderPasswordField({
                    name: 'confirmPassword',
                    label: 'Confirm New Password',
                    placeholder: 'Confirm new password',
                    autoComplete: 'new-password',
                  })}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      disableElevation
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, minWidth: 180 }}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </SettingsSection>

            <SettingsSection title="Appearance" description="Customise the interface theme properties of the platform">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {theme === 'dark' ? 'Easier on the eyes in low light conditions' : 'Classic clean light interface representation'}
                  </Typography>
                </Box>
                <Switch checked={theme === 'dark'} onChange={toggleTheme} color="primary" />
              </Box>
            </SettingsSection>
          </Stack>
        )}

        {activeTab === 1 && isCompanyAdmin && (
          <Stack spacing={3.5}>
            <SettingsSection title="Table Configurations" description="Adjust parameters mapping display matrices across register views">
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <AssetsPageSizeManager currentUser={currentUser} showToast={showToast} />
              </Paper>
            </SettingsSection>

            <SettingsSection
              title="Asset Ownership"
              description="Control whether assets are assigned to a specific owner"
            >
              <AssetOwnershipManager
                currentUser={currentUser}
                showToast={showToast}
              />
            </SettingsSection>

            <SettingsSection
              title="Approval Workflow"
              description="Control whether assets require an approval workflow selection"
            >
              <ApproverManager
                currentUser={currentUser}
                showToast={showToast}
              />
            </SettingsSection>

            <SettingsSection
              title="Public QR Access"
              description="Control whether unauthenticated visitors can view asset details by scanning a QR code"
            >
              <PublicQrAccessManager
                currentUser={currentUser}
                showToast={showToast}
              />
            </SettingsSection>

            <SettingsSection
              title="Ticket Duplication Policy"
              description="Control whether new help desk tickets can be raised for an asset that already has an open ticket"
            >
              <DuplicateTicketPolicyManager
                currentUser={currentUser}
                showToast={showToast}
              />
            </SettingsSection>
          </Stack>
        )}

        {activeTab === 2 && isCompanyAdmin && (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: 'background.paper' }}>
            <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap', alignItems: 'center', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                size="small" variant="outlined"
                sx={{ flex: 1, minWidth: 260, bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                placeholder="Search catalog lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'action.active' }} fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {listsLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={72} sx={{ borderBottom: '1px solid #eee' }} />
                  ))}
                </Box>
              ) : filteredLists.length > 0 ? (
                filteredLists.map((list) => {
                  const isFallback = isSystemFallbackList(list);
                  const isApprover = isApproverList(list);
                  const isReceivers = isTicketReceiversList(list);
                  return (
                    <PreferenceListItem
                      key={list.id || list.name}
                      icon={isReceivers ? <GroupIcon fontSize="small" /> : <ListAltIcon fontSize="small" />}
                      tag={list.scope || 'system'}
                      title={list.name}
                      subtitle={list.descriptions || formatLabel(list.name)}
                      metaValue={
                        isReceivers
                          ? `${(list.items || []).filter(it => !NON_RECEIVER_ROLES.includes(String(it.value || '').toLowerCase())).length} roles`
                          : `${countRealItems(list.items || [])} values`
                      }
                      onEdit={() => setEditingList(list)}
                      onDelete={
                        !isFallback && !isApprover && !isReceivers && list.scope !== 'system'
                          ? () => setDeletingList(list)
                          : undefined
                      }
                    />
                  );
                })
              ) : (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No settings match your search parameters.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {activeTab === 3 && isCompanyAdmin && (
          <CompanyProfileManager
            currentUser={currentUser}
            showToast={showToast}
          />
        )}
      </Box>

      {editingList && (
        <EditListItemsModal
          open={Boolean(editingList)}
          list={editingList}
          saving={listSaving}
          currentUser={currentUser}
          onClose={() => !listSaving && setEditingList(null)}
          onSave={handleSaveList}
        />
      )}

      <Dialog open={Boolean(deletingList)} onClose={() => setDeletingList(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete List Override?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete override settings for <strong>{formatLabel(deletingList?.name)}</strong>?
            This will wipe tenant-specific configurations and roll back options to global defaults.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeletingList(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" color="error" disableElevation onClick={handleDeleteList} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Delete Override
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;